import { types, getEnv, flow, Instance } from "mobx-state-tree";

import { Endpoint, endpoints } from "../api/ApiEndpoints";
import { StorageModel } from "../storage/StorageModel";
import { emptyProject, ProjectResponse } from "../../api-models/response/project/ProjectResponse";
import { permissionsService } from "../authorization/UserPermissionsService";
import { ViewProject, ViewProjectAssignedTo } from "../authorization/Permissions";
import ProjectInput from "./ProjectInput";
import { Project } from "./Project";

const CurrentProjectModel = types.model({
  projectId: types.frozen<number>(),
  currentProject: types.optional(types.frozen<Project>(), new Project(emptyProject)),
});

export const CurrentProjectStorageModel = types
  .compose(CurrentProjectModel, StorageModel)
  .named("CurrentProjectStorageModel")
  .actions((self) => {
    return {
      mapResponse(response: ProjectResponse) {
        self.currentProject = new Project(response);
      },
      editProject: flow(function* editProject(projectInput: ProjectInput) {
        const endpoint = endpoints.get("project");
        endpoint.routeParams = [self.currentProject.id];
        yield getEnv(self).api.put(endpoint, projectInput);
        yield self.load();
      }),
      deleteProject: flow(function* deleteProject() {
        const endpoint = endpoints.get("project");
        endpoint.routeParams = [self.currentProject.id];
        yield getEnv(self).api.delete(endpoint);
      }),
      getCustomPropertiesInitialValues() {
        const extractedFields = {};
        self.currentProject.customPropertyValues.forEach((property) => {
          const label = property.label;
          const value = property.value || "";
          extractedFields[label] = value;
        });
        return extractedFields;
      },
    };
  })
  .views((self) => ({
    get projectIsLoaded() {
      return self.currentProject.id > 0;
    },

    get endpoint() {
      const endpoint: Endpoint = endpoints.get("project");
      endpoint.routeParams = [self.projectId];
      return endpoint;
    },

    get canUserViewProject() {
      return (
        permissionsService.userHasPermissionTo(ViewProject) ||
        permissionsService.userHasPermissionTo(ViewProjectAssignedTo)
      );
    },
  }))
  .views((self) => ({
    get canLoadItems() {
      return self.canUserViewProject && self.projectId > 0;
    },
    get isLoading() {
      return !self.projectIsLoaded;
    },
    get hasCustomProperties() {
      return !!self.currentProject.customPropertyValues.length;
    },
  }));

export type CurrentProjectStorage = Instance<typeof CurrentProjectStorageModel>;
